import React, { useEffect, useState } from 'react';
import { BACKEND_URL } from '../../config';

const money = (v) => `${v < 0 ? '-' : ''}$${Math.abs(Number(v || 0)).toFixed(2)}`;
const pnlColor = (v) => (v > 0 ? '#16a34a' : v < 0 ? '#dc2626' : '#475569');
const REASON = { TP: 'TP hit', SL: 'SL hit', MANUAL: 'Closed' };

const Tile = ({ label, value, color, testid }) => (
  <div style={{ padding: '1rem', background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '0.35rem' }}>{label}</div>
    <div data-testid={testid} style={{ fontSize: '1.6rem', fontWeight: 700, color }}>{value}</div>
  </div>
);

const TradeLine = ({ t }) => (
  <div data-testid={`my-trade-${t.order_id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' }}>
    <span style={{ fontWeight: 700, color: t.direction === 'BUY' ? '#16a34a' : '#dc2626', minWidth: 42 }}>{t.direction}</span>
    <span style={{ fontWeight: 600, color: '#0f172a', minWidth: 64 }}>{t.pair}</span>
    <span style={{ color: '#64748b', flex: 1 }}>{REASON[t.close_reason] || 'Closed'} · {new Date(t.closed_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
    <span style={{ fontWeight: 700, color: pnlColor(t.realized_pnl) }}>{money(t.realized_pnl)}</span>
  </div>
);

export const MyTradesCard = ({ period }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const start = period?.start_date;
  const end = period?.end_date;

  useEffect(() => {
    const qs = start && end ? `?start_date=${start}&end_date=${end}` : '';
    fetch(`${BACKEND_URL}/api/sim/performance${qs}`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { setData(d); setError(null); })
      .catch(() => setError('Could not load your trades right now.'));
  }, [start, end]);

  const s = data?.summary;
  return (
    <div data-testid="my-trades-card" style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', borderRadius: '12px', border: '2px solid #3b82f6', marginBottom: '2rem' }}>
      <h3 style={{ fontSize: '1.25rem', color: '#1e3a8a', marginBottom: '0.25rem' }}>🎯 My Trades</h3>
      <p style={{ fontSize: '0.8rem', color: '#475569', marginBottom: '1rem' }}>
        Trades you placed on the Calculator{data?.period ? ` · ${data.period.start_date} to ${data.period.end_date}` : ''}
      </p>
      {error && <div data-testid="my-trades-error" style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</div>}
      {s && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            <Tile label="Closed Trades" value={s.total_trades} color="#1e40af" testid="my-trades-total" />
            <Tile label="Win Rate" value={`${s.win_rate}%`} color="#0f766e" testid="my-trades-win-rate" />
            <Tile label="Wins / Losses" value={`${s.won} / ${s.lost}`} color="#334155" testid="my-trades-wl" />
            <Tile label="Net P&L" value={money(s.net_pnl)} color={pnlColor(s.net_pnl)} testid="my-trades-net-pnl" />
          </div>
          {s.live_count > 0 && (
            <div data-testid="my-trades-live" style={{ fontSize: '0.85rem', color: '#1e3a8a', marginBottom: '0.75rem' }}>
              🟢 {s.live_count} live trade{s.live_count > 1 ? 's' : ''} running · floating <b style={{ color: pnlColor(s.floating_pnl) }}>{money(s.floating_pnl)}</b>
            </div>
          )}
          <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', maxHeight: 320, overflowY: 'auto' }}>
            {data.trades.length ? data.trades.map((t) => <TradeLine key={t.order_id} t={t} />)
              : <div data-testid="my-trades-empty" style={{ padding: '1.25rem', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>No closed trades in this period yet.</div>}
          </div>
        </>
      )}
    </div>
  );
};
